// Portierung der Wortüberlappungs-Logik aus
// amplify/backend/function/bedrockAgent/src/topicKnowledge.js (+ `normalise`
// aus dimensionKnowledge.js) im Repo aivy-backend-app. Reine Funktionen ohne
// AWS-Abhängigkeit, dort bereits durch topicKnowledge.test.js abgesichert —
// bei einer inhaltlichen Änderung dort BEIDE Dateien synchron halten.
//
// Zweck hier: dieselbe Logik, die produktiv entscheidet "trifft eine
// Kundenfrage ein Wissensbasis-Dokument", wird wiederverwendet, um mehrere
// unbeantwortete Fragen im OS-Dashboard als "gleiches Anliegen" zu clustern —
// kein Modellaufruf, keine neue Infrastruktur, dieselbe bereits bewährte
// Schwelle (mindestens ein gemeinsames Inhaltswort, siehe selectTopics dort).

const STOPWORDS = new Set([
  'dass',
  'wird',
  'werden',
  'wurde',
  'wurden',
  'kann',
  'kannst',
  'koennen',
  'koennt',
  'koennte',
  'koennten',
  'koenntest',
  'sind',
  'habe',
  'haben',
  'hast',
  'hat',
  'hatte',
  'nicht',
  'auch',
  'oder',
  'aber',
  'denn',
  'weil',
  'wenn',
  'dann',
  'noch',
  'schon',
  'sich',
  'diese',
  'dieser',
  'dieses',
  'einer',
  'eines',
  'einem',
  'einen',
  'eine',
  'ueber',
  'unter',
  'waehrend',
  'zwischen',
  'ihrer',
  'ihrem',
  'ihren',
  'meine',
  'meiner',
  'meinem',
  'meinen',
  'deine',
  'unser',
  'unsere',
  'fuer',
  'vom',
  'beim',
  'sein',
  'ist',
  'was',
  'wie',
  'welche',
  'welcher',
  'welches',
  'man',
  'mir',
  'mich',
  'ich',
  'braucht'
])

/** Kleinschreibung plus Diakritika weg, damit "Vertraglichkeit" auch "Verträglichkeit" findet. */
const normalise = (value) =>
  typeof value === 'string'
    ? value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim()
    : ''

/** ALL-CAPS Akronyme (API, ATS, KI, ...) sind immer signifikant, egal wie kurz. */
const acronymsIn = (value) => {
  if (typeof value !== 'string') return []
  return (value.match(/[A-ZÄÖÜ]{2,}/g) || []).map((wort) => wort.toLowerCase())
}

/** Inhaltstragende Wörter: ab 4 Buchstaben ohne Füllwörter, plus Akronyme jeder Länge. */
const significantWords = (value) => {
  const woerter = normalise(value).match(/[a-z]{4,}/gu) || []
  const inhaltswoerter = woerter.filter((wort) => !STOPWORDS.has(wort))
  return Array.from(new Set([...acronymsIn(value), ...inhaltswoerter]))
}

/** Anzahl gemeinsamer inhaltstragender Wörter zwischen zwei Texten. `0` ohne Text. */
const frageOverlap = (frage, question) => {
  if (typeof frage !== 'string' || frage.length === 0) return 0
  const frageWoerter = significantWords(frage)
  const fragWoerterSet = new Set(significantWords(question))
  return frageWoerter.filter((wort) => fragWoerterSet.has(wort)).length
}

/**
 * Wörter, die in mindestens zwei der übergebenen Texte als signifikant
 * gelten — genau das Signal, das das Clustern in assistant.js ausgelöst hat.
 * Für die Hervorhebung im OS-Dashboard, keine Retrieval-Logik.
 */
const sharedSignificantWords = (texts) => {
  const counts = new Map()
  ;(texts || []).forEach((text) => {
    new Set(significantWords(text)).forEach((wort) => {
      counts.set(wort, (counts.get(wort) || 0) + 1)
    })
  })
  return Array.from(counts.entries())
    .filter(([, count]) => count >= 2)
    .map(([wort]) => wort)
}

export { normalise, significantWords, frageOverlap, sharedSignificantWords }
